import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = '/api/v1/user/share';

export interface ShareAnalysisOptions {
  notes?: string;
  includeFollowups?: boolean;
}

export interface ShareAnalysisResult {
  shareToken: string;
  shareUrl: string;
}

export interface CheckShareResult {
  isShared: boolean;
  shareToken?: string;
  shareUrl?: string;
  includeFollowups?: boolean;
}

export function useAnalysisSharing() {
  const { isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    };
  }, []);

  const shareAnalysis = useCallback(
    async (sessionId: string, options?: ShareAnalysisOptions): Promise<ShareAnalysisResult | null> => {
      if (!isAuthenticated) return null;

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE}/analysis`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            sessionId,
            notes: options?.notes,
            includeFollowups: options?.includeFollowups ?? true,
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Nie udalo sie udostepnic analizy');
        }

        const data = await response.json();
        return { shareToken: data.shareToken, shareUrl: data.shareUrl };
      } catch (err) {
        setError((err as Error).message);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, getAuthHeaders]
  );

  const unshareAnalysis = useCallback(
    async (sessionId: string): Promise<boolean> => {
      if (!isAuthenticated) return false;

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE}/analysis/${sessionId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error('Nie udalo sie cofnac udostepnienia');
        }

        return true;
      } catch (err) {
        setError((err as Error).message);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, getAuthHeaders]
  );

  const checkAnalysisShared = useCallback(
    async (sessionId: string): Promise<CheckShareResult | null> => {
      if (!isAuthenticated) return null;

      try {
        const response = await fetch(`${API_BASE}/analysis/${sessionId}/check`, {
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          return { isShared: false };
        }

        return await response.json();
      } catch {
        return { isShared: false };
      }
    },
    [isAuthenticated, getAuthHeaders]
  );

  const updateShareOptions = useCallback(
    async (sessionId: string, options: ShareAnalysisOptions): Promise<boolean> => {
      if (!isAuthenticated) return false;

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE}/analysis/${sessionId}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(options),
        });

        if (!response.ok) {
          throw new Error('Nie udalo sie zaktualizowac opcji udostepniania');
        }

        return true;
      } catch (err) {
        setError((err as Error).message);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, getAuthHeaders]
  );

  return {
    isLoading,
    error,
    shareAnalysis,
    unshareAnalysis,
    checkAnalysisShared,
    updateShareOptions,
  };
}
